/**
 * AddBulk Utilities
 * Pure utility functions for bulk upload functionality
 */

import { getFileFromURL } from "helpers/getFileFromURL";
import { SelectOption } from "components/dropdown/Dropdown";
import { PAGES, UploadPayload, UploadData } from "./AddBulk.types";
import { ENTITY_TYPE_CONFIG } from "./AddBulk.config";
import { getContactTypeLabelById } from "./AddBulk.validation";

/**
 * Build API payload based on entity type and form values
 * @param values - Form values
 * @param noun - Entity type
 * @param parentSelectedIdName - Parent ID field name (optional)
 * @param parentSelectedId - Parent ID value (optional)
 * @param extraPayloadItems - Additional payload items (optional)
 * @param contactAccessTypes - Contact access types for contact-specific logic
 * @returns Constructed payload object
 */
export const buildPayload = (
  values: UploadData,
  noun: PAGES,
  parentSelectedIdName?: string,
  parentSelectedId?: number,
  extraPayloadItems?: { [fieldName: string]: any },
  contactAccessTypes?: SelectOption[]
): UploadPayload => {
  const payload: UploadPayload = {};
  const config = ENTITY_TYPE_CONFIG[noun];

  // Handle entity-specific logic
  if (noun === PAGES.CONTACT && contactAccessTypes) {
    const contactType = getContactTypeLabelById(values.contactTypesId, contactAccessTypes);
    switch (contactType) {
      case "site":
        payload.SitesId = values.sitesID || null;
        payload.ProjectsId = null;
        break;
      case "project":
        payload.SitesId = null;
        payload.ProjectsId = values.projectsID || null;
        break;
      default:
        payload.SitesId = null;
        payload.ProjectsId = null;
    }
    payload.ContactTypesId = values.contactTypesId;
    payload[config.fileFieldName] = values.csvFile;
  } else if (noun === PAGES.SURVEY) {
    payload.Action = values.action;
    payload.ProgrammesId = values.programmesId;
    payload.SurveyTemplatesId = values.surveyTemplatesId;
    payload[config.fileFieldName] = values.csvFile;
  } else {
    // For all other entity types
    payload[config.fileFieldName] = values.csvFile;
  }

  // Add parent ID if provided
  if (parentSelectedIdName && parentSelectedId) {
    payload[parentSelectedIdName] = parentSelectedId;
  }

  // Merge extra payload items if provided
  if (extraPayloadItems) {
    Object.keys(extraPayloadItems).forEach((key) => {
      payload[key] = extraPayloadItems[key];
    });
  }

  return payload;
};

/**
 * Download template file for bulk upload
 * @param url - Template file URL
 * @param noun - Entity type for filename
 * @returns Promise that resolves when download completes
 */
export const downloadTemplateFile = async (url: string, noun: PAGES): Promise<void> => {
  const file = await getFileFromURL(url, "test_file.csv");
  downloadFile(file, "text/csv", `Add_${noun}_bulk_template.csv`);
};

/**
 * Download a file to the user's device
 * @param file - File content
 * @param fileType - MIME type
 * @param fileName - Filename to save as
 */
export const downloadFile = (file: any, fileType: string, fileName: string): void => {
  const blob = new Blob([file], { type: fileType });
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);
  }, 100);
};

/**
 * Export error messages to a text file
 * @param messages - Array of error messages
 * @param fileName - Filename for the export
 */
export const exportErrorsToFile = (messages: string[], fileName: string): void => {
  const sanitizedMessages = messages.map((message) =>
    message
      .replace(/\\n/g, "\n")
      .split("\n")
      .map((line) => line.trim())
      .join("\n")
  );
  const content = sanitizedMessages.join("\n");
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Format error message for display
 * @param item - Error message string
 * @returns Formatted error display (string or JSX)
 */
export const formatErrorMessage = (item: string): string => {
  const unescapedItem = item.replace(/\\n/g, "\n");
  return unescapedItem;
};

/**
 * Parse item display for line-by-line error formatting
 * Used for displaying multi-line errors with proper structure
 * @param item - Error message with newlines
 * @returns Array of line parts for rendering
 */
export const parseErrorItemDisplay = (
  item: string
): Array<{ columnName: string; value: string }> => {
  const itemLines = item.split("\n");
  return itemLines.map((line) => {
    const parts = line.split(": ");
    return {
      columnName: parts[0] ? parts[0].replace(/([a-z])([A-Z])/g, "$1 $2") : "",
      value: parts[1] || "",
    };
  });
};
